import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Modal, 
  ModalBody, 
  ModalFooter, 
  ModalHeader, 
  Label,
  InputGroup, 
  InputGroupAddon, 
  Button,
} from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { CustomInput } from 'reactstrap';

import { withGetData } from '../../main/hoc/withGetData';
import { withSendRequest } from '../../main/hoc/withSendRequest';
import { setUnselected } from '../actions/experimentsList';
import { filterChartDataByVarId } from '../utils';
import CompareExperimentsCharts from '../components/compareExperimentsCharts';
import { ErrorBoundary } from '../../main/hoc/errorboundary';

const CompareExperimentsModal = ({ isOpen, toggle, variables, sendRequest, variablesNames }) => {
  const [ selectedVariable, setSelectedVariable ] = useState('');
  const [ selectedVariableName, setSelectedVariableName ] = useState('');
  const [ selectedVariableDefinition, setSelectedVariableDefinition ] = useState('');
  const [ selectedVariableUnit, setSelectedVariableUnit ] = useState('');
  const [ allVariables, setAllVariables ] = useState([]);
  const [ chartsData, setChartsData ] = useState([]);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState('');
  const [ showCombinedChart, setShowCombinedChart ] = useState(false);

  const { t } = useTranslation();

  const dispatch = useDispatch();
  
  const selectedExperiments = useSelector((state) => state.experiments.get('selectedExperiments'));

  useEffect(() => {
    if (variables.data) {
      setAllVariables(variables.data);
    }
  }, [ variables ]);

  useEffect(() => {
    if (!isOpen) {
      setChartsData([]);
      setError('');
      setSelectedVariable('');
      setSelectedVariableName('');
      setSelectedVariableDefinition('');
      setSelectedVariableUnit('');
    }
  }, [ selectedExperiments, isOpen ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setChartsData([]);

    if (!allVariables.includes(selectedVariable)) {
      setError('Variable does not exist!');
    }
    else {
      try {
        for (let id of selectedExperiments) {
          const { data: chartsData } = await sendRequest('get', `/experiments/${id}/variables?ids_name=summary&page_size=1000`);
          const response = await sendRequest('get', `/experiments/${id}`);
          const experimentInfo = { ...response.data };

          setChartsData((prev) => [ ...prev, {
            data: filterChartDataByVarId(chartsData.items, selectedVariable.id),
            experimentInfo: experimentInfo,
            id
          } ]);
        }
        setLoading(false);
      } 
      catch (err) {
        const error = err.response ? err.response.data : err.message;
        setError(error);
      }
    }
  };

  const handleRemoveClick = (id) => {
    dispatch(setUnselected(id));
    setChartsData((prev) => prev.filter(experiment => experiment.id !== id));
  };

  const handleSelectChange = (e) => {
    const value = e?.value || '';
    setSelectedVariableName(value.name || value.ids_field);
    setSelectedVariableDefinition(value.definition || value.ids_field);
    setSelectedVariableUnit(value.unit || '-');
    setSelectedVariable(value);
  };

  return (
    <Modal isOpen={isOpen} backdrop={true} toggle={toggle} size='xl' className='compare-modal'>
      <ModalHeader toggle={toggle}>{t('compare_experiments_modal_header')}</ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit}>
          <Label htmlFor='variables'>{t('compare_experiments_modal_variables_label')}{':'}</Label>
          <InputGroup>
            <Select id='variables' onChange={handleSelectChange} className="ac-select-container"
              classNamePrefix="ac-select" isSearchable={true} isClearable={true}
              options={ allVariables?.map(
                (variable) => { return { "value": variable, "label": variable.ids_field };})} />
            <InputGroupAddon addonType='append'>
              <Button type='submit' color='primary'>{t('compare_experiments_modal_show_button')}</Button>
            </InputGroupAddon>
          </InputGroup>
          {chartsData.length > 0 && (
            <div className='mt-3'>
              <CustomInput
                type='switch'
                id='show-combined-chart'
                name='show-combined-chart'
                label={t('show_combined_chart_label')}
                checked={showCombinedChart}
                onChange={(e) => setShowCombinedChart(e.target.checked)}
              />
            </div>
          )}
        </form>
        <CompareExperimentsCharts
          chartsData={chartsData}
          error={error}
          loading={loading}
          selectedVariableName={selectedVariableName}
          selectedVariableDefinition={selectedVariableDefinition}
          selectedVariableUnit={selectedVariableUnit}
          selectedExperimentsCount={selectedExperiments.size}
          handleRemoveClick={handleRemoveClick}
          showCombinedChart={showCombinedChart}
        />
      </ModalBody>
      <ModalFooter>
        <div className='d-flex justify-content-end'>
          <Button type='button' onClick={toggle}>Close</Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

CompareExperimentsModal.propTypes = {
  toggle: PropTypes.func,
  isOpen: PropTypes.bool,
  variables: PropTypes.object, // HOC
  sendRequest: PropTypes.func, // HOC
  variablesNames: PropTypes.object,
};

export default compose(
  withSendRequest,
  withGetData({ variables: '/variables?ids_name=summary' }),
  ErrorBoundary((props) => props.t('components.compare_experiments_modal'))
)(CompareExperimentsModal);
